import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { SERVICES, SUBURBS, BRAND } from "@/lib/site";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import QuoteForm from "@/components/QuoteForm";
import CallStrip from "@/components/CallStrip";

const SITE_URL =
  (typeof window !== "undefined" && window.location.origin) ||
  "https://flowgridplumbing.com.au";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = service.icon;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        title={`${service.title} — Sydney Metro Plumber`}
        description={`${service.title} across Sydney Metro. ${service.description} Call ${BRAND.phone} for upfront pricing.`}
        canonicalPath={`/services/${service.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: service.title,
          provider: {
            "@type": "Plumber",
            name: BRAND.name,
            telephone: BRAND.phone,
            email: BRAND.email,
          },
          areaServed: SUBURBS.map((s) => ({ "@type": "Place", name: s.name })),
          description: service.description,
          url: `${SITE_URL}/services/${service.slug}`,
          offers: {
            "@type": "Offer",
            availability: "https://schema.org/InStock",
            priceCurrency: "AUD",
            url: `${SITE_URL}/services/${service.slug}`,
          },
        }}
      />
      <PageHeader
        eyebrow={service.title}
        title={`${service.title} — Sydney Metro`}
        subtitle={service.description}
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={BRAND.phoneTel}
            data-testid="service-detail-call-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-bold text-white hover:bg-blue-700"
          >
            <Phone className="h-5 w-5" /> Call {BRAND.phone}
          </a>
          <a
            href="#quote"
            data-testid="service-detail-quote-btn"
            className="inline-flex items-center gap-2 rounded-lg border-2 border-white/80 px-6 py-3 font-bold text-white hover:bg-white hover:text-slate-900"
          >
            Free Quote <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </PageHeader>

      <section className="bg-white py-20 md:py-24">
        <div className="fg-container grid gap-12 md:grid-cols-12">
          <div className="md:col-span-7">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
              <Icon className="h-6 w-6" />
            </div>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900">
              {service.title}
            </h2>
            <ul className="mt-6 space-y-3">
              {service.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                  <span className="text-base text-slate-700">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                Available in your suburb
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {SUBURBS.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/service-areas/${s.slug}`}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-700 hover:border-blue-400 hover:text-blue-700"
                    data-testid={`service-suburb-pill-${s.slug}`}
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <aside className="md:col-span-5">
            <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-md">
              <h3 className="font-display text-2xl font-bold text-slate-900">
                Free quote for {service.title.toLowerCase()}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Drop your details — we'll call you back fast.
              </p>
              <div className="mt-5">
                <QuoteForm compact idPrefix={`svc-${slug}`} />
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-24">
        <div className="fg-container">
          <h3 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Other services we offer
          </h3>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {others.map((s) => {
              const OIcon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="fg-card-hover group rounded-2xl border border-slate-200 bg-white p-6"
                  data-testid={`related-service-${s.slug}`}
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700 group-hover:bg-blue-600 group-hover:text-white">
                    <OIcon className="h-4 w-4" />
                  </div>
                  <div className="mt-4 font-display text-lg font-bold text-slate-900">
                    {s.title}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">{s.short}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CallStrip />
    </>
  );
}
